import React, { useRef } from 'react'
import classNames from 'classnames'
import Imgix from 'react-imgix'
import { Spinner } from 'react-bootstrap'
import { useDrag, useDrop } from 'react-dnd'
import ExperienceDeleteIcon from './ExperienceDeleteIcon'

export const ENROL_QUESTION_DND = 'ENROL_QUESTION_ROW'

const stripHtmlToText = (html) => {
  if (!html) return ''
  return String(html)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .trim()
}

const TYPE_ICON_PATHS = {
  text: '<path d="M4 7V5h16v2M9 19h6M12 5v14"/>',
  email:
    '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>',
  phone:
    '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.7 2.35a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.75.34 1.54.57 2.35.7A2 2 0 0 1 22 16.92z"/>',
  country:
    '<circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20"/>',
  object:
    '<path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z"/><path d="M9 21v-8h6v8"/>',
  singleSelectionDropdown:
    '<circle cx="12" cy="12" r="9"/><path d="m8.5 12.5 2.5 2.5 5-6"/>',
  multipleSelectionDropdown:
    '<rect x="4" y="4" width="16" height="16" rx="2"/><path d="m8 12 3 3 5-6"/>',
  files:
    '<path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h6"/>',
  date: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4M16 3v4M3 10h18"/>',
  scale:
    '<circle cx="5" cy="5" r="2"/><circle cx="19" cy="19" r="2"/><path d="M5 7v11h11M6 18l6-6"/>',
}

const EnrolTypeIcon = ({ answerType }) => {
  const paths = TYPE_ICON_PATHS[answerType] || TYPE_ICON_PATHS.text
  return (
    <span
      className='experience-qicon'
      title={answerType || 'text'}
      aria-hidden='true'
      dangerouslySetInnerHTML={{
        __html: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${paths}</svg>`,
      }}
    />
  )
}

const QuestionCard = ({
  question,
  index,
  actualIndex,
  isEditing = false,
  onEdit,
  onAddBelow,
  onDelete,
  isUploading,
  allQuestions = [],
  moveItem,
  onDragEnd,
}) => {
  const ref = useRef(null)
  const dndIndex = actualIndex != null ? actualIndex : index

  const [{ handlerId }, drop] = useDrop({
    accept: ENROL_QUESTION_DND,
    collect(monitor) {
      return { handlerId: monitor.getHandlerId() }
    },
    hover(item, monitor) {
      if (!ref.current || isUploading || typeof moveItem !== 'function') {
        return
      }
      const dragIndex = item.index
      const hoverIndex = dndIndex
      if (dragIndex === hoverIndex) return

      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      moveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: ENROL_QUESTION_DND,
    item: () => ({ index: dndIndex }),
    canDrag: !isUploading && typeof moveItem === 'function',
    end() {
      if (
        !isUploading &&
        typeof moveItem === 'function' &&
        typeof onDragEnd === 'function'
      ) {
        onDragEnd()
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  const handleCardClick = () => {
    onEdit(question, index)
  }

  const handleActionClick = (e, action) => {
    e.stopPropagation()
    action()
  }

  const getParentQuestionIndex = () => {
    if (!question.dependentQuestionId || !allQuestions.length) return null

    const parentIndex = allQuestions.findIndex((q) => {
      const questionId = q.id || q._id
      return (
        questionId &&
        questionId.toString().trim() ===
          question.dependentQuestionId.toString().trim()
      )
    })

    return parentIndex >= 0 ? parentIndex + 1 : null
  }

  const titleText = stripHtmlToText(question.text) || 'Untitled Question'
  const descriptionText = stripHtmlToText(question.description)
  const isRequired = question.isRequired !== false
  const parentNo = getParentQuestionIndex()

  return (
    <div
      ref={ref}
      data-handler-id={handlerId}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      className={classNames('experience-lrow experience-enrol-q', {
        'is-editing': isEditing,
        'is-dragging': isDragging,
      })}
      onClick={handleCardClick}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleCardClick()
        }
      }}>
      <span className='experience-grab' title='Drag to reorder'>
        ⋮⋮
      </span>
      <span className='experience-qnum'>{index + 1}</span>
      <EnrolTypeIcon answerType={question.answerType} />
      <div className='experience-lrow-copy'>
        <b>{titleText}</b>
        {descriptionText ? <small>{descriptionText}</small> : null}
        {question.questionType === 'dependent' && parentNo ? (
          <small className='experience-qdep'>
            Depends on Question Number {parentNo}
          </small>
        ) : null}
      </div>
      {question.imageFileName ? (
        question.imageFileName.startsWith('data:') ? (
          <img
            src={question.imageFileName}
            alt=''
            className='experience-lrow-thumb experience-enrol-q-thumb'
          />
        ) : (
          <Imgix
            imgixParams={{
              fit: 'crop',
              crop: 'center',
              auto: 'format,compress',
              q: 90,
            }}
            src={`${process.env.REACT_APP_IMG_SOURCE}/forms/images/${question.imageFileName}`}
            width={88}
            height={56}
            alt=''
            className='experience-lrow-thumb experience-enrol-q-thumb lazyload'
          />
        )
      ) : (
        <span
          className='experience-enrol-q-thumb is-empty'
          aria-hidden='true'
        />
      )}
      <span className={`experience-req${isRequired ? ' on' : ''}`}>
        {isRequired ? 'Required' : 'Optional'}
      </span>
      <button
        type='button'
        className='experience-add-below'
        title='Add new below'
        aria-label='Add new below'
        disabled={isUploading}
        onClick={(e) => handleActionClick(e, onAddBelow)}>
        +
      </button>
      <button
        type='button'
        className='experience-x'
        title='Delete'
        aria-label='Delete'
        disabled={isUploading}
        onClick={(e) => handleActionClick(e, onDelete)}>
        {isUploading ? (
          <Spinner animation='border' size='sm' />
        ) : (
          <ExperienceDeleteIcon />
        )}
      </button>
    </div>
  )
}

export default QuestionCard
